// =============================================================================
// LOGO UPLOADER — file picker + preview + URL fallback for tenant logos
//
// Replaces the plain "paste a URL" input on the Branding page. Tenant
// admins can:
//   • Drag-and-drop or click to upload an image (PNG / JPG / SVG / WebP / GIF)
//   • Paste a URL (kept for back-compat — some tenants self-host)
//   • Preview the current logo at the configured size
//   • Remove the logo (writes empty string to the form field)
//
// Storage: Platform DB bucket `tenant-branding`, public read, RLS-gated
// writes (only owner/admin of the tenant_id in the path can write).
// Path layout: tenant-branding/<tenant_id>/<purpose>-<timestamp>.<ext>
// =============================================================================

import { useRef, useState, useEffect, type ChangeEvent, type DragEvent } from 'react';
import { Upload, Link2, Trash2, ImageIcon, Loader2, Check, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/utils/helpers';
import { generatePwaVariant, pwaVariantPath } from '@/lib/pwaIconGenerator';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB — matches bucket cap
const ACCEPT_TYPES  = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif,image/x-icon,image/vnd.microsoft.icon';

export function LogoUploader({
  value, onChange, tenantId, purpose, label, sub, previewBg, recommendedSize,
}: {
  /** Current logo URL — empty string for "no logo". */
  value: string;
  onChange: (next: string) => void;
  /** Owner tenant — required so the upload lands in the right folder
   *  (RLS uses the first path segment to gate writes). */
  tenantId: string;
  /** Filename prefix — 'logo' / 'logo-dark' / 'favicon'. Lets us tell
   *  multiple uploads apart in the bucket without inspecting JSON. */
  purpose: 'logo' | 'logo-dark' | 'favicon';
  label: string;
  sub?: string;
  /** Optional background color for the preview tile (use when the logo
   *  is white-on-transparent and would disappear on white). */
  previewBg?: 'light' | 'dark';
  /** Optional explicit dimension hint shown in the dropzone, e.g.
   *  "512×512 PNG, transparent background". When set, replaces the
   *  generic file-types hint. */
  recommendedSize?: string;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [dragOver, setDragOver]   = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft]   = useState(value);
  /** Actual decoded dimensions of the loaded preview image — shown in
   *  the preview tile so the user can see whether what they uploaded
   *  matches the recommendation. SVGs report intrinsic viewBox dims
   *  via naturalWidth / naturalHeight too, so this works for vectors. */
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  // Reset dimensions when the URL changes — otherwise we'd briefly show
  // the old image's dims while the new one is still loading.
  useEffect(() => { setDims(null); }, [value]);

  const uploadFile = async (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Pick an image file (PNG, JPG, SVG, WebP, GIF, ICO).');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`File too large — max ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)} MB.`);
      return;
    }
    if (!tenantId) {
      setError('Save the tenant before uploading a logo.');
      return;
    }

    setUploading(true);
    try {
      const ext      = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
      const stamp    = Date.now();
      const path     = `${tenantId}/${purpose}-${stamp}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('tenant-branding')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });
      if (upErr) throw upErr;

      const { data: urlData } = supabase.storage.from('tenant-branding').getPublicUrl(path);
      if (!urlData?.publicUrl) throw new Error('Upload succeeded but no public URL was returned.');

      // PWA install-safe companion variant. Only generated for the
      // main logo (favicons and dark variants don't drive the
      // manifest icons that Chrome checks for installability).
      //
      // The image is composited onto an opaque 512×512 white
      // rounded-square background — that's what Chrome's install
      // dialog requires. Without this, transparent or small
      // tenant logos silently fail installability and the user
      // gets a "fallback instructions" modal instead of the
      // native install prompt.
      //
      // Best-effort: if anything throws (canvas-blocked browser,
      // weird SVG that won't decode, network hiccup on upload),
      // we log and keep going. The main logo is already saved;
      // the tenant just won't get the install-friendly variant
      // and falls back to the same Chrome behaviour they had
      // before this code existed.
      if (purpose === 'logo') {
        try {
          const pwaBlob = await generatePwaVariant(file);
          const pwaPath = pwaVariantPath(path);
          const { error: pwaErr } = await supabase.storage
            .from('tenant-branding')
            .upload(pwaPath, pwaBlob, {
              cacheControl: '3600',
              upsert: true, // overwrite a stale variant from a previous upload at the same stamp
              contentType: 'image/png',
            });
          if (pwaErr) {
            console.warn('[LogoUploader] PWA variant upload failed (main logo still saved):', pwaErr);
          }
        } catch (pwaGenErr) {
          console.warn('[LogoUploader] PWA variant generation failed (main logo still saved):', pwaGenErr);
        }
      }

      onChange(urlData.publicUrl);
      setUrlDraft(urlData.publicUrl);
    } catch (e: any) {
      console.error('[LogoUploader] upload failed:', e);
      setError(e?.message || 'Upload failed. Try a smaller file.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  };

  const onDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  const clear = () => {
    onChange('');
    setUrlDraft('');
    setError(null);
  };

  const previewBgClass = previewBg === 'dark'
    ? 'bg-surface-900'
    : 'bg-surface-50 dark:bg-surface-100';

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2">
        <label className="text-xs font-medium text-surface-700">{label}</label>
        {sub && <span className="text-[11px] text-surface-500">{sub}</span>}
      </div>

      {value ? (
        // ── Preview + actions ──
        <div className={cn(
          'rounded-xl border border-surface-200 p-3 flex items-center gap-3',
          previewBgClass,
        )}>
          <div className="w-14 h-14 rounded-lg bg-surface-0 border border-surface-200 flex items-center justify-center overflow-hidden shrink-0">
            <img
              src={value}
              alt="Logo preview"
              className="max-w-full max-h-full object-contain"
              onLoad={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.naturalWidth && img.naturalHeight) {
                  setDims({ w: img.naturalWidth, h: img.naturalHeight });
                }
              }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-surface-900 truncate">
              {value.startsWith('http') ? new URL(value).pathname.split('/').pop() : value}
            </p>
            <div className="flex items-center gap-2">
              {dims && (
                <span className="text-[11px] font-mono text-surface-600 shrink-0">
                  {dims.w}×{dims.h}
                </span>
              )}
              <p className="text-[11px] text-surface-500 truncate">
                {recommendedSize ? `Recommended: ${recommendedSize}` : value}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="px-2 py-1 rounded-md text-[11px] font-medium text-surface-700 hover:bg-surface-200 disabled:opacity-50 transition-colors"
              title="Replace"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : 'Replace'}
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={uploading}
              className="p-1.5 rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
              title="Remove"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ) : (
        // ── Empty state — drop zone ──
        <label
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={cn(
            'flex flex-col items-center justify-center gap-1.5 px-4 py-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors',
            dragOver
              ? 'border-brand-500 bg-brand-500/10'
              : 'border-surface-300 hover:border-surface-400 hover:bg-surface-50/60 dark:hover:bg-surface-100/30',
            uploading && 'opacity-60 cursor-wait',
          )}
        >
          {uploading ? (
            <>
              <Loader2 size={18} className="animate-spin text-brand-600" />
              <span className="text-xs text-surface-600">Uploading…</span>
            </>
          ) : (
            <>
              <Upload size={18} className="text-surface-500" />
              <span className="text-xs text-surface-700">
                <strong>Click to upload</strong> or drag &amp; drop
              </span>
              <span className="text-[11px] text-surface-500 text-center">
                {recommendedSize ?? 'PNG, JPG, SVG, WebP up to 5 MB'}
              </span>
            </>
          )}
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT_TYPES}
            onChange={onPick}
            disabled={uploading}
            className="sr-only"
          />
        </label>
      )}

      {/* URL fallback toggle — paste a URL for self-hosted assets */}
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => setShowUrlInput(s => !s)}
          className="inline-flex items-center gap-1 text-surface-500 hover:text-surface-700 transition-colors"
        >
          <Link2 size={11} />
          {showUrlInput ? 'Hide URL input' : 'Or paste a URL'}
        </button>
        {value && (
          <span className="ml-auto inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
            <Check size={11} /> Saved
          </span>
        )}
      </div>

      {showUrlInput && (
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={urlDraft}
            onChange={e => setUrlDraft(e.target.value)}
            placeholder="https://cdn.example.com/logo.png"
            className="input-field text-sm flex-1"
          />
          <button
            type="button"
            onClick={() => {
              if (urlDraft.trim()) {
                onChange(urlDraft.trim());
                setError(null);
              }
            }}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-surface-100 hover:bg-surface-200 text-surface-700 transition-colors"
          >
            Apply
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1.5 text-[11px] text-red-700 dark:text-red-400">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!value && !error && !showUrlInput && (
        <div className="flex items-start gap-1.5 text-[11px] text-surface-500">
          <ImageIcon size={11} className="mt-0.5 shrink-0" />
          <span>No logo yet — upload one or paste a URL above.</span>
        </div>
      )}
    </div>
  );
}
